import {filter, find, isEmpty, map, some, sortBy, transform} from 'lodash';
import {useCallback, useEffect, useState, Fragment, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi, sessionApi, userApi} from 'api';
import {
    ActionButton, Button, Checkbox, ConfirmModal, Icon, PageHeader,
    Pagination, ParticipantInfo, PredefinedNotifierType
} from 'components';
import {APPOINTMENT_INTEGRATIONS} from 'constants/integrations';
import {AUTH_SOURCE_TRANSLATE} from 'constants/translate';
import {UserContext} from 'context';
import {useApiData, useUserInfoSchema} from 'hooks';
import {FullSessionAppointment, SessionAppointment, AccountAssignmentInfo, AnyParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getAssignmentName} from 'services';
import {PaginatedData, PaginationOptions, ParticipantRole, UserSummary} from 'types';
import {dateUtils, sessionUtils} from 'utils';
import {formatDate} from 'utils/transform';

import {ConnectInfoModal} from './AppointmentConnectInfoModal';
import {AppointmentModal} from './AppointmentModal';
import {AppointmentsAccordion} from './AppointmentsAccordion';
import {AppointmentsTable} from './AppointmentsTable';
import {NotifyParticipantsModal} from './NotifyParticipantsModal';
import {AppointmentClientNotificationAction} from './types';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.APPOINTMENTS;
const T_KEYS_TABLE = T_KEYS.TABLE;

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

type ConnectInfoModalProps = {
    open: boolean;
    data?: FullSessionAppointment;
}

type Callback = (confirm: boolean) => void;

export const Appointments: React.FC = () => {
    const {t} = useTranslation();
    const [participantLinkWithToken, setParticipantLinkWithToken] = useState<boolean>(false);
    const [myOnly, setMyOnly] = useState<boolean>(true);
    const {
        settings: {
            common: {
                timeZone, dateFormat, timeFormat
            },
            appointment: appointmentSettings,
            featuresAvailability
        }
    } = useContext(UserContext);
    useUserInfoSchema({fetchOnInit: true, guestEnabled: featuresAvailability.guestEnabled});

    const agentsNotificationEnabled = useMemo(() => appointmentSettings?.agent?.notification?.email?.enabled, [appointmentSettings]);
    const clientsNotificationEnabled = useMemo(() => appointmentSettings?.client?.notification?.email?.enabled, [appointmentSettings]);
    const [appointmentModalProps, setAppointmentModalProps] = useState<{open: boolean, session?: SessionAppointment}>({open: false});
    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState({open: false, data: null});
    const [appointments, setAppointments] = useState<FullSessionAppointment[]>([]);
    const [connectInfoModalProps, setConnectInfoModalProps] = useState<ConnectInfoModalProps>({
        open: false
    });
    const [notifyClientModalProps, setNotifyClientModalProps] = useState<{
        open: boolean,
        callback?: Callback,
        action?: AppointmentClientNotificationAction
    }>({open: false});
    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);

    const dateTimeFormat = useMemo(() => dateUtils.getDateTimeFormat(dateFormat, timeFormat), [dateFormat, timeFormat]);

    const {send: fetchGroups, data: groups} = useApiData(groupApi.getGroups);
    const {send: fetchUsers, data: users = []} = useApiData<UserSummary[]>(userApi.getUsersSummary);

    const fetchAppointmentsFn = useCallback(() =>
        sessionApi.getAppointments({
            participantLinkWithToken,
            myOnly,
            pagination: {page: pagination.page, pageSize: pagination.pageSize}
        }), [participantLinkWithToken, myOnly, pagination.page, pagination.pageSize]);

    const onSuccessFetchAppointments = useCallback((data: PaginatedData<FullSessionAppointment>) => {
        setAppointments(data.items);
        setPagination((prev) => ({
            ...prev,
            pages: data.pages,
            count: data.count,
        }));
    }, []);

    const {send: fetchAppointments, loading} = useApiData<PaginatedData<FullSessionAppointment>>(fetchAppointmentsFn, {
        notificationType: PredefinedNotifierType.FetchError,
        onSuccess: onSuccessFetchAppointments
    });

    const resetConfirmDeleteModal = useCallback(() => {
        setConfirmDeleteModalProps({open: false, data: null});
    }, []);

    const createAppointmentFn = useCallback((session: SessionAppointment, notify?: boolean) =>
        sessionApi.createAppointment({session, options: {participantLinkWithToken}}, {notify}),
    [participantLinkWithToken]);

    const updateAppointmentFn = useCallback((session: SessionAppointment, notify?: boolean) =>
        sessionApi.updateAppointment({session, options: {participantLinkWithToken}}, {notify}),
    [participantLinkWithToken]);

    const onSuccessDeleteSession = useCallback(() => {
        const sessionId = confirmDeleteModalProps.data;
        setAppointments((prev) => filter(prev, (appointment) => appointment.session.id !== sessionId));
        resetConfirmDeleteModal();
    }, [confirmDeleteModalProps, resetConfirmDeleteModal]);

    const {send: deleteSession} = useApiData(sessionApi.deleteSession, {
        onSuccess: onSuccessDeleteSession,
        notificationType: PredefinedNotifierType.Delete
    });

    const hasParticipantsToNotify = useCallback((participants: AnyParticipant[]) =>
        some(participants, (participant) =>
            sessionUtils.isUserParticipant(participant) &&
            agentsNotificationEnabled ||
            sessionUtils.isInviteeParticipant(participant) &&
                clientsNotificationEnabled &&
                !!sessionUtils.computeParticipantEmail(participant)),
    [agentsNotificationEnabled, clientsNotificationEnabled]);

    const canManage = useCallback((session: SessionAppointment) => !APPOINTMENT_INTEGRATIONS.includes(session.creator?.authSource), []);

    const handleDelete = useCallback((sessionId: string) => {
        setConfirmDeleteModalProps({open: true, data: sessionId});
    }, []);

    const handleEdit = useCallback((session: SessionAppointment) => {
        setAppointmentModalProps({open: true, session});
    }, []);

    const closeConfirmDeleteModal = useCallback((confirm: boolean, sessionId: string) => {
        if (confirm) {
            const currentSession = find(appointments, (appointment) => appointment.session.id === sessionId)?.session;
            if (hasParticipantsToNotify(currentSession.participants)) {
                resetConfirmDeleteModal();
                setNotifyClientModalProps({
                    open: true,
                    action: AppointmentClientNotificationAction.Delete,
                    callback: (_confirm) => deleteSession(sessionId, {notify: _confirm})
                });
            } else {
                deleteSession(sessionId);
            }
        } else {
            resetConfirmDeleteModal();
        }
    }, [appointments, hasParticipantsToNotify, deleteSession, resetConfirmDeleteModal]);

    const handleOpenConnectInfoModal = useCallback((data: FullSessionAppointment) => {
        setConnectInfoModalProps({open: true, data});
    }, []);

    const closeConnectInfoModal = useCallback(() => {
        setConnectInfoModalProps({open: false});
    }, []);

    const renderScheduledDates = useCallback((schedule: {
        startDate?: string;
        endDate?: string;
    }) => (
        <Fragment>
            <div>{schedule?.startDate && formatDate(schedule.startDate, timeZone, dateTimeFormat)}</div>
            <div>{schedule?.endDate && formatDate(schedule.endDate, timeZone, dateTimeFormat)}</div>
        </Fragment>
    ), [dateTimeFormat, timeZone]);

    const renderCreateInfo = useCallback((createdAt: string, authSource?: string) => (
        <Fragment>
            <div>{formatDate(createdAt, timeZone, dateTimeFormat)}</div>
            {authSource && <div>{`${t(T_KEYS_TABLE.CONTENT.SOURCE)}: ${authSource}`}</div>}
        </Fragment>
    ), [t, timeZone, dateTimeFormat]);

    const renderAssignmentInfo = useCallback((assignment: AccountAssignmentInfo) => (
        getAssignmentName(groups, assignment.groupId, assignment?.subgroupId)
    ), [groups]);

    const renderParticipants = useCallback((session: SessionAppointment, className?: string) => {
        const participants = transform(session.participants, (result, participant) => {
            if (participant.role === ParticipantRole.Assistant && featuresAvailability.assistantEnabled) {
                result.push(participant);
            } else if (participant.role === ParticipantRole.Guest && featuresAvailability.guestEnabled) {
                result.push(participant);
            } else {
                result.push(participant);
            }
        }, []);

        return map(sortBy(participants, 'role'), (participant) => (
            <ParticipantInfo
                key={participant.id}
                className={className}
                session={session}
                iconClassName='tw-mr-2'
                fields={{icon: true, clientInfoModal: true}}
                spacing={false}
                participant={participant}
                local={sessionUtils.isParticipantLocalUser(participant)}
            />
        ));
    }, [featuresAvailability.assistantEnabled, featuresAvailability.guestEnabled]);

    const renderActions = useCallback((session: SessionAppointment) => (
        <div className='tw-flex tw-justify-end'>
            <Icon
                name='edit'
                color='primary'
                button
                group
                stopPropagation
                disabled={!canManage(session)}
                onClick={() => handleEdit(session)}
            />
            <Icon
                name='delete'
                color='alert'
                button
                group
                stopPropagation
                disabled={!canManage(session)}
                onClick={() => handleDelete(session.id)}
            />
        </div>
    ), [canManage, handleEdit, handleDelete]);

    const setPage = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            page: value
        }));
    }, [setPagination]);

    const setPageSize = useCallback((value: number) => {
        setPagination((prev) => ({
            ...prev,
            pageSize: value
        }));
    }, [setPagination]);

    const getSourceName = useCallback((session: SessionAppointment) =>
        session.creator?.authSource && AUTH_SOURCE_TRANSLATE[session.creator.authSource] ?
            t(AUTH_SOURCE_TRANSLATE[session.creator.authSource]) :
            session.creator?.authSource, [t]);

    const openCreateAppointmentModal = useCallback(() => {
        setAppointmentModalProps({open: true});
    }, []);

    const closeNotifyClientModal = useCallback((confirm: boolean, callback: Callback) => {
        callback?.(confirm);
        setNotifyClientModalProps({open: false});
    }, []);

    const onSaveSuccess = useCallback(() => {
        setAppointmentModalProps({open: false});
        fetchAppointments();
    }, [fetchAppointments]);

    const {send: createAppointment, loading: createLoading, error: createError, reset: createReset} =
        useApiData(createAppointmentFn, {onSuccess: onSaveSuccess, notificationType: PredefinedNotifierType.Save});

    const {send: updateAppointment, loading: updateLoading, error: updateError, reset: updateReset} =
        useApiData(updateAppointmentFn, {onSuccess: onSaveSuccess, notificationType: PredefinedNotifierType.Save});

    const closeAppointmentModal = useCallback((confirm: boolean, appointment: SessionAppointment) => {
        if (confirm) {
            const isNotificationNeeded = hasParticipantsToNotify(appointment.participants);
            if (appointmentModalProps.session) {
                if (isNotificationNeeded) {
                    setNotifyClientModalProps({
                        open: true,
                        action: AppointmentClientNotificationAction.Update,
                        callback: (_confirm) => updateAppointment(appointment, _confirm)
                    });
                } else {
                    updateAppointment(appointment);
                }
            } else {
                if (isNotificationNeeded) {
                    setNotifyClientModalProps({
                        open: true,
                        action: AppointmentClientNotificationAction.Create,
                        callback: (_confirm) => createAppointment(appointment, _confirm)
                    });
                } else {
                    createAppointment(appointment);
                }
            }
        } else {
            setAppointmentModalProps({open: false});
        }
    }, [hasParticipantsToNotify, appointmentModalProps.session, updateAppointment, createAppointment]);

    const resetAppointmentModal = useCallback(() => {
        updateReset();
        createReset();
    }, [updateReset, createReset]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments, participantLinkWithToken, myOnly]);

    useEffect(() => {
        fetchGroups();
        fetchUsers();
    }, [fetchGroups, fetchUsers]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <div className='tw-flex -md:tw-flex-col-reverse -md:tw-items-baseline md:tw-items-center tw-justify-end tw-mb-4'>
                <div className='tw-flex tw-flex-row tw-flex-wrap tw-flex-1 tw-justify-end'>
                    <Checkbox
                        size='small'
                        name='token'
                        checked={participantLinkWithToken}
                        label={t(T_KEYS.PARTICIPANTS_LINKS_CHECKBOX)}
                        className='tw-mr-2'
                        onChange={() => setParticipantLinkWithToken(!participantLinkWithToken)}
                    />
                    <Checkbox
                        size='small'
                        name='myOnly'
                        checked={myOnly}
                        label={t(T_KEYS.MY_ONLY_CHECKBOX)}
                        className='tw-mr-2'
                        onChange={() => setMyOnly(!myOnly)}
                    />
                </div>
                <div className='tw-flex tw-items-start tw-flex-wrap'>
                    <Button
                        Icon={<Icon name='rotate' size='sm' className='tw-mr-2' />}
                        disabled={loading}
                        className='-md:tw-mb-2 tw-mr-2'
                        onClick={fetchAppointments}
                    >
                        {t(T_KEYS.REFRESH_BTN)}
                    </Button>
                    <ActionButton action='create' className='-md:tw-mb-2' onClick={openCreateAppointmentModal}>
                        {t(T_KEYS.CREATE_BTN)}
                    </ActionButton>
                </div>
            </div>
            {!isEmpty(appointments) &&
                <Pagination
                    maxItemsToDisplay={9}
                    className='tw-ml-auto tw-flex-1'
                    itemsClassName='tw-min-w-64 tw-justify-between sm:tw-justify-end'
                    rangeClassName='-sm:tw-hidden'
                    pageSelectorClassName='-sm:tw-hidden'
                    pageSize={pagination.pageSize}
                    pageNumber={pagination.page}
                    totalItems={pagination.count}
                    onPageNumberChange={setPage}
                    onPageSizeChange={setPageSize}
                />}
            <div className='-lg:tw-hidden'>
                <AppointmentsTable
                    loading={loading}
                    appointments={appointments}
                    renderAssignmentInfo={renderAssignmentInfo}
                    renderCreateInfo={renderCreateInfo}
                    renderScheduledDates={renderScheduledDates}
                    renderParticipants={renderParticipants}
                    renderActions={renderActions}
                    getSourceName={getSourceName}
                    onOpenConnectInfoModal={handleOpenConnectInfoModal}
                />
            </div>
            <div className='lg:tw-hidden'>
                <AppointmentsAccordion
                    loading={loading}
                    appointments={appointments}
                    renderAssignmentInfo={renderAssignmentInfo}
                    renderCreateInfo={renderCreateInfo}
                    renderScheduledDates={renderScheduledDates}
                    renderParticipants={renderParticipants}
                    renderActions={renderActions}
                    getSourceName={getSourceName}
                    onOpenConnectInfoModal={handleOpenConnectInfoModal}
                />
            </div>
            <AppointmentModal
                {...appointmentModalProps}
                groups={groups}
                users={users}
                error={createError || updateError}
                loading={createLoading || updateLoading}
                reset={resetAppointmentModal}
                onClose={closeAppointmentModal}
            />
            <ConfirmModal title={t(T_KEYS.CONFIRM_REMOVE_DIALOG.TITLE)} {...confirmDeleteModalProps} onClose={closeConfirmDeleteModal} />
            {connectInfoModalProps.open &&
                <ConnectInfoModal
                    {...connectInfoModalProps}
                    onClose={closeConnectInfoModal}
                />}
            <NotifyParticipantsModal {...notifyClientModalProps} onClose={closeNotifyClientModal} />
        </Fragment>
    );
};